import React, { type SyntheticEvent } from "react";
import * as CompareEntryStyles from "../../styles/CompareEntry";
import type {
    CarDataType,
    CompareMotorizationType,
} from "../../../../../../common-deprecated/features/compare/utils/newCarCompareTypes";
import { type CompareV2DispatchType, useCompareV2Selector } from "../../../../redux/store";
import { getSingleCarCashPriceAndId } from "../../../../utils/formatters/priceAndEcoSpecsFormatters";
import { CommonMonthlyPriceLabelKey, hideDiscountInfo } from "../../../../../../common-deprecated/priceUtils";
import { useCompareV2OptionalLabel } from "../../../../utils/labels";
import { getModelMapValueById } from "../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import {
    formatAEMCashPrice,
    getAemMonthlyRateFormat,
    getAemPriceConfig,
    hasDualCurrency,
} from "../../../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { AemFmComponent } from "../../../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import { getDefaultPriceObject } from "../../../../../../common-deprecated/constants";
import { showModal } from "../../../../../../common-deprecated/redux/actions/ModalActions";
import { MODAL_FINANCE_DISCLAIMER } from "../../../../../../common-deprecated/utils/modalConstants";
import Icon from "../../../../../../common-deprecated/styles/v2/lexus/globals/Icon";
import * as Price from "../../../../../../common-deprecated/styles/v2/lexus/components/Price";
import { formatMonthlyFinancePrice } from "../../../../../shared-logic/utils/priceUtils";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

type PriceSpecType = {
    car: CarDataType;
    motorization: CompareMotorizationType;
};

const PriceSpec = (props: PriceSpecType): JSX.Element => {
    const { car, motorization } = props;
    const dispatch = useDispatch<CompareV2DispatchType>();
    const commonSettings = useCompareV2Selector((state) => state.commonSettings);
    const { hideFinanceTooltip } = commonSettings;
    const titleLabel = useCompareV2OptionalLabel("compareBodyTopFrom");
    const modelCode = getModelMapValueById(commonSettings, car.modelId)?.internalCode;

    const priceConfig = getAemPriceConfig(commonSettings, AemFmComponent.Compare, modelCode);
    const cash = getSingleCarCashPriceAndId(car, motorization, priceConfig.priceType);
    const hideDiscount = hideDiscountInfo(priceConfig);
    const hasSecondaryPrice = hasDualCurrency(commonSettings);
    const isDiscounted = cash.discount !== cash.price && !hideDiscount;

    const normalPrice = formatAEMCashPrice(cash.price, commonSettings.culture.name, priceConfig);
    const secondaryPrice = formatAEMCashPrice(cash.price, commonSettings.culture.name, priceConfig, undefined, true);
    const discountedPrice = formatAEMCashPrice(cash.discount, commonSettings.culture.name, priceConfig);
    const monthlyPrice = car.finance
        ? formatMonthlyFinancePrice(
              { ...getDefaultPriceObject(), monthly: car.finance.rate },
              car.finance.financeInfo,
              commonSettings,
              getAemMonthlyRateFormat(commonSettings, AemFmComponent.Compare, "new"),
          )
        : undefined;

    const openDisclaimer = (event: SyntheticEvent): void => {
        event.preventDefault();
        if (!car.finance?.financeInfo?.disclaimer) return;
        dispatch(
            showModal({
                type: MODAL_FINANCE_DISCLAIMER,
                disclaimer: car.finance?.financeInfo?.disclaimer,
                productName: car.finance?.financeInfo?.productName || "",
            }),
        );
    };

    return (
        <CompareEntryStyles.Wrapper>
            {priceConfig.priceEnabled && (
                <CompareEntryStyles.KeyValue>
                    <CompareEntryStyles.Title>{titleLabel}</CompareEntryStyles.Title>
                    <CompareEntryStyles.ValueWrapper>
                        <CompareEntryStyles.Value isLoading={car.loading} discountedPrice={isDiscounted}>
                            <span dangerouslySetInnerHTML={{ __html: normalPrice }} />
                        </CompareEntryStyles.Value>
                        {isDiscounted && !car.loading && (
                            <CompareEntryStyles.Value>
                                <span dangerouslySetInnerHTML={{ __html: discountedPrice }} />
                            </CompareEntryStyles.Value>
                        )}
                    </CompareEntryStyles.ValueWrapper>
                    {/* Dual currency price */}
                    {hasSecondaryPrice && (
                        <CompareEntryStyles.Value isLoading={car.loading}>
                            <span dangerouslySetInnerHTML={{ __html: secondaryPrice }} />
                        </CompareEntryStyles.Value>
                    )}
                </CompareEntryStyles.KeyValue>
            )}

            {!car.loading && car.finance && monthlyPrice && ((car.finance?.rate || 0) > 0 || car.finance.loading) && (
                <CompareEntryStyles.KeyValue>
                    <CompareEntryStyles.Value isLoading={car.finance.loading}>
                        {monthlyPrice.labels.map(
                            (label) =>
                                label.key === CommonMonthlyPriceLabelKey.Price &&
                                !car.finance?.loading && (
                                    <Price.MonthlyPriceWrapper key={label.key}>
                                        <span dangerouslySetInnerHTML={{ __html: label.value }} />
                                        {!hideFinanceTooltip && car.finance?.financeInfo.disclaimer && (
                                            <CompareEntryStyles.Disclaimer onClick={openDisclaimer}>
                                                <Icon variant="info" />
                                            </CompareEntryStyles.Disclaimer>
                                        )}
                                    </Price.MonthlyPriceWrapper>
                                ),
                        )}
                    </CompareEntryStyles.Value>
                    {monthlyPrice.labels.map(
                        (label) =>
                            label.key !== CommonMonthlyPriceLabelKey.Price && (
                                <CompareEntryStyles.LegalText key={label.key}>
                                    <span dangerouslySetInnerHTML={{ __html: label.value }} />
                                </CompareEntryStyles.LegalText>
                            ),
                    )}
                </CompareEntryStyles.KeyValue>
            )}
        </CompareEntryStyles.Wrapper>
    );
};

export default PriceSpec;
